import { NETWORK_RECIPIENTS_LABELS, NotificationService, type RecipientTypes, useIntl } from '@capsicum/common';
import { Card, ModalService, Notification, useRouter } from '@capsicum/common-web';
import {
	DeleteRecipientModal,
	RecipientForm,
	type RecipientFormShape
} from '@capsicum/features/digital-collectibles/web';
import {
	RECEIVE_NETWORK_LABELS,
	type ReceiveNetworks,
	useWalletWithdrawEditRecipientMutation,
	useWalletWithdrawRecipient
} from '@capsicum/features/wallet/shared';
import { useParams } from 'next/navigation';
import styled from 'styled-components';

export const EditRecipient = () => {
	const { back, searchParams } = useRouter();
	const params = useParams();

	const recipientId = (params?.recipientId || '') as string;
	const networkType = searchParams?.get('selectedNetwork') as ReceiveNetworks;

	const {
		data: recipient,
		isFetching: isUpdateRecipientFetching,
		isLoading: isRecipientLoading,
		refetch: updateRecipient
	} = useWalletWithdrawRecipient(recipientId);

	const { isPending: isEditRecipientLoading, mutate: saveRecipient } = useWalletWithdrawEditRecipientMutation();

	const { $t } = useIntl();

	const texts = {
		alert: $t('wallet.withdraw.recipient.editRecipient.alert', { network: RECEIVE_NETWORK_LABELS[networkType] }),
		cancel: $t('wallet.withdraw.recipient.editRecipient.delete'),
		createButtonLabel: $t('wallet.withdraw.recipient.editRecipient.createButtonLabel'),
		notificationSuccess: $t('wallet.withdraw.recipient.editRecipient.notificationSuccess'),
		title: $t('wallet.withdraw.recipient.editRecipient.pageTitle')
	};

	const onFormSubmission = (data: RecipientFormShape) => {
		saveRecipient(
			{
				id: recipientId,
				payload: {
					label: data.recipientName.trim(),
					type: data.type,
					walletOrExchangeName: data.exchange
				}
			},
			{
				onSuccess: () => {
					updateRecipient();
					back();
					NotificationService.addNotification({
						component: <Notification message={texts.notificationSuccess} variant="success" />
					});
				}
			}
		);
	};

	const deleteRecipient = () => {
		ModalService.addModal({
			renderComponent: ({ close }) => (
				<DeleteRecipientModal
					testID="wallet-withdraw-deleteRecipient"
					onButtonPress={() => {
						saveRecipient(
							{
								id: recipientId,
								payload: {
									label: recipient?.label,
									status: 'disabled',
									type: recipient?.type
								}
							},
							{
								onSuccess: () => {
									close();
									back();
									NotificationService.addNotification({
										component: (
											<Notification
												message={$t('wallet.withdraw.recipient.deleteRecipient.notification')}
												variant="success"
											/>
										)
									});
								}
							}
						);
					}}
					onSecondaryActionOnButtonPress={close}
				/>
			)
		});
	};

	return (
		<S.Container>
			<S.Title>{texts.title}</S.Title>
			<S.EditRecipientCard>
				<RecipientForm
					alertText={texts.alert}
					defaultValues={{
						address: recipient?.blockchainAddress,
						exchange: recipient?.exchange,
						network: NETWORK_RECIPIENTS_LABELS[networkType],
						recipientName: recipient?.label,
						type: recipient?.type as RecipientTypes
					}}
					isEdit
					isSaving={isEditRecipientLoading || isUpdateRecipientFetching}
					networkType={networkType}
					secondaryButtonLabel={texts.cancel}
					submitButtonLabel={texts.createButtonLabel}
					onSecondaryClick={deleteRecipient}
					onSubmitForm={onFormSubmission}
				/>
			</S.EditRecipientCard>
		</S.Container>
	);
};

namespace S {
	export const Container = styled.div`
		display: flex;
		flex-direction: column;
		width: 100%;
		padding: ${({ theme }) => theme.spacings.medium};
	`;

	export const EditRecipientCard = styled(Card)`
		display: flex;
		flex-direction: column;
		padding: ${({ theme }) => theme.spacings.medium};
		max-width: 100%;
	`;
	export const Title = styled.p`
		${({ theme }) => theme.fonts.headings.title5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		margin: 0 0 ${({ theme }) => theme.spacings.medium};
	`;
}
